import { useTransformImage } from "@/hooks/bulkResizer/useTransformImage";
import { flipToggleHorizontal, flipToggleVertical, setPreview } from "@/store/features/bulkResize/actions";
import { getFlipOperation } from "@/store/features/bulkResize/utils/operations/getFlipOperation";
import { useAppDispatch, useAppSelector } from "@/store/store";
import { AnalyticsHelper } from "@/utils/adsense/AnalyticsHelper";
import { sleep } from "@/utils/sleep";
import { useBulkResizerImageProvider } from "../../BulkResizerImageProvider";

export const useBulkResizerFlipSettings = (imageId: string) => {
    const image = useAppSelector((store) => store.bulkResize.images.find((image) => image.id === imageId));
    if (!image) throw new Error("image not found");
    const dispatch = useAppDispatch();
    const { transformImage } = useTransformImage();
    const { operation, setOperation } = useBulkResizerImageProvider();
    const flippingState = operation === "flip-h" ? "horizontal" : operation === "flip-v" ? "vertical" : "";

    const handleHorizontalToggle = async () => {
        if (operation) return;
        setOperation("flip-h");
        await sleep();

        await flip("horizontal");
        dispatch(flipToggleHorizontal({ imageId }));
    };

    const handleVerticalToggle = async () => {
        if (operation) return;
        setOperation("flip-v");
        await sleep();

        await flip("vertical");
        dispatch(flipToggleVertical({ imageId }));
    };

    const flip = async (direction: "horizontal" | "vertical") => {
        AnalyticsHelper.collectData({
            pageKey: "bulk-resize-editor",
            event: "Flip",
            featureName: "Bulk Resizer",
            eventLabel: "Editor Page",
        });

        const newOperation = getFlipOperation(image, direction);
        const src = await transformImage(image.id, newOperation);

        setOperation(null);
        dispatch(setPreview({ id: imageId, value: { src } }));
        await sleep();
    };

    return {
        handleHorizontalToggle,
        handleVerticalToggle,
        flippingState,
    };
};
